import { useState, useMemo } from 'react';
import MetricCard from '../shared/MetricCard';
import { StatusBadge } from '../shared/Badge';
import AgingBadge from '../shared/AgingBadge';
import VendorFilterBar from '../shared/VendorFilterBar';
import EmptyState from '../shared/EmptyState';
import { formatMoney, formatDate, daysSince } from '../../utils/formatters';
import { ALERT_THRESHOLD_DAYS } from '../../utils/constants';
import { api } from '../../api/client';

export default function CreditLifecycle({ data, refresh }) {
  const [vendorFilter, setVendorFilter] = useState(null);
  const [sortBy, setSortBy] = useState('aging');

  const items = data?.items || data?.returns || [];

  const outstanding = items.filter((r) => (r._creditStatus || r.credit_status || 'EXPECTED') !== 'RECONCILED');

  const vendors = useMemo(() => {
    const map = {};
    outstanding.forEach((r) => {
      const name = r.vendor_name || r.vendorName || 'Unknown';
      if (!map[name]) map[name] = { name, total: 0, count: 0, oldest: 0 };
      map[name].total += (r.total_usd || r.returned_total_usd || r.returnedTotal / 100 || 0);
      map[name].count += 1;
      const d = daysSince(r.return_date || r._returnDate);
      if (d > map[name].oldest) map[name].oldest = d;
    });
    return Object.values(map);
  }, [outstanding]);

  const filtered = vendorFilter
    ? outstanding.filter((r) => (r.vendor_name || r.vendorName) === vendorFilter)
    : outstanding;

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (sortBy === 'aging') return daysSince(b.return_date || b._returnDate) - daysSince(a.return_date || a._returnDate);
      if (sortBy === 'amount') return (b.total_usd || b.returned_total_usd || 0) - (a.total_usd || a.returned_total_usd || 0);
      return (a.vendor_name || '').localeCompare(b.vendor_name || '');
    });
  }, [filtered, sortBy]);

  const totalOutstanding = outstanding.reduce((s, r) => s + (r.total_usd || r.returned_total_usd || 0), 0);
  const alertCount = outstanding.filter((r) => daysSince(r.return_date || r._returnDate) >= ALERT_THRESHOLD_DAYS).length;
  const reconciled = items.filter((r) => (r._creditStatus || r.credit_status) === 'RECONCILED');
  const onStatement = items.filter((r) => (r._creditStatus || r.credit_status) === 'ON_STATEMENT');

  const handleUpdateStatus = async (returnOrderId, newStatus) => {
    try {
      await api.updateCreditStatus(returnOrderId, { status: newStatus, user: 'Dashboard User' });
      refresh();
    } catch (err) {
      alert('Failed to update: ' + err.message);
    }
  };

  return (
    <div>
      <div className="flex gap-3 mb-6 flex-wrap">
        <MetricCard
          label="Outstanding Credits"
          value={formatMoney(totalOutstanding)}
          sub={`${outstanding.length} returns pending`}
          accent="#C62828"
          icon={'\u21A9'}
        />
        <MetricCard
          label="On Statement"
          value={onStatement.length.toString()}
          sub="Awaiting reconciliation"
          accent="#1565C0"
        />
        <MetricCard
          label="Overdue (30+ days)"
          value={alertCount.toString()}
          sub="Needs follow-up"
          accent="#E65100"
          icon={'\u23F1'}
        />
        <MetricCard
          label="Reconciled"
          value={reconciled.length.toString()}
          sub="Credits closed"
          accent="#2E7D32"
        />
      </div>

      <VendorFilterBar vendors={vendors} active={vendorFilter} onFilter={setVendorFilter} />

      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-gray-400 font-semibold">Sort:</span>
        {['aging', 'amount', 'vendor'].map((s) => (
          <button
            key={s}
            onClick={() => setSortBy(s)}
            className={`px-3 py-1 rounded-md text-xs font-semibold ${
              sortBy === s ? 'bg-gray-200 text-gray-800' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {s === 'aging' ? 'Oldest First' : s === 'amount' ? 'Highest $' : 'Vendor'}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        {sorted.map((r) => {
          const aging = daysSince(r.return_date || r._returnDate);
          const isAlert = aging >= ALERT_THRESHOLD_DAYS;
          const status = r._creditStatus || r.credit_status || 'EXPECTED';
          const total = r.total_usd || r.returned_total_usd || 0;
          const returnOrderId = r.id || r.return_order_id;

          return (
            <div
              key={returnOrderId}
              className={`bg-white rounded-xl p-5 border ${
                isAlert ? 'border-red-200 border-l-4 border-l-red-500' : 'border-gray-200 border-l-4 border-l-gray-300'
              }`}
            >
              <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-5">
                <div>
                  <div className="font-bold text-sm text-gray-800 mb-0.5">
                    {r.part_name || r.partNamePrimary || r.partName}
                    {(r.return_type === 'CORE' || r.is_core) && (
                      <span className="ml-2 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-bold">
                        CORE
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">
                    {r.part_number || r.partNumber} &middot; RO #{r.ro_number || r.repairOrderNumber || r.roNumber} &middot; {r.customer_name || r.repairOrderCustomerFullName || r.customer}
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1">
                    {r.vendor_name || r.vendorName} &middot; PO #{r.po_number || r.orderNumber || r.poNumber}
                  </div>
                  {(r.note || r.reason) && (
                    <div className="text-[10px] text-gray-400 italic mt-1">
                      &ldquo;{r.note || r.reason}&rdquo;
                    </div>
                  )}
                </div>

                <div className="text-right">
                  <div className="text-lg font-extrabold text-gray-800">
                    {formatMoney(total)}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    Returned {formatDate(r.return_date || r._returnDate)}
                  </div>
                </div>

                <AgingBadge days={aging} />

                <div className="flex flex-col items-center gap-1.5">
                  <StatusBadge status={status} />
                  {status === 'EXPECTED' && (
                    <button
                      onClick={() => handleUpdateStatus(returnOrderId, 'ON_STATEMENT')}
                      className="text-[10px] text-blue-600 font-semibold hover:underline"
                    >
                      Mark on Statement &rarr;
                    </button>
                  )}
                  {status === 'ON_STATEMENT' && (
                    <button
                      onClick={() => handleUpdateStatus(returnOrderId, 'RECONCILED')}
                      className="text-[10px] text-green-600 font-semibold hover:underline"
                    >
                      Mark Reconciled &#10003;
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {sorted.length === 0 && <EmptyState icon="&#10003;" text="All credits reconciled" />}
    </div>
  );
}
